import { NavLink, useNavigate } from "react-router-dom";
import { useSession } from "@/context/SessionContext";
import {
  LayoutDashboard,
  CalendarRange,
  TrendingUp,
  UploadCloud,
  BadgeDollarSign,
  ShieldCheck,
  Cloud,
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import api from "@/lib/api";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Portfolio Overview", icon: LayoutDashboard, end: true, id: "overview" },
  { to: "/trends", label: "Trends & Compare", icon: TrendingUp, id: "trends" },
  { to: "/value", label: "MSP Value & Savings", icon: BadgeDollarSign, id: "value" },
  { to: "/monthly-review", label: "Monthly Import Review", icon: CalendarRange, id: "monthly-review" },
  { to: "/import", label: "Import Wizard", icon: UploadCloud, id: "import" },
];

export default function Layout({ children }) {
  const { session, selectPersona, resetDemoSession, asOf, setAsOf } = useSession();
  const [collapsed, setCollapsed] = useState(false);
  const [personas, setPersonas] = useState([]);
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    api.get("/personas").then((res) => setPersonas(res.data)).catch(() => {});
  }, []);

  const { data: months = [] } = useQuery({
    queryKey: ["months", session?.persona],
    queryFn: async () => (await api.get("/months")).data,
    enabled: !!session,
  });

  const monthKeys = months.map((m) => m.month_key); // desc: newest first
  const effectiveKey = asOf === "latest" ? (monthKeys[0] || null) : asOf;
  const curIdx = effectiveKey ? monthKeys.indexOf(effectiveKey) : -1;
  const canNewer = curIdx > 0;
  const canOlder = curIdx >= 0 && curIdx < monthKeys.length - 1;
  const currentMonthLabel =
    asOf === "latest"
      ? "Select Month"
      : (months.find((m) => m.month_key === asOf)?.month_label || "Select Month");
  const goNewer = () => { if (canNewer) setAsOf(monthKeys[curIdx - 1]); };
  const goOlder = () => { if (canOlder) setAsOf(monthKeys[curIdx + 1]); };

  // Safety net: if the selected month is not in the available data (e.g. data was
  // reset or re-imported), fall back to "Latest available" instead of an empty view.
  useEffect(() => {
    if (asOf !== "latest" && months.length > 0 && !monthKeys.includes(asOf)) {
      setAsOf("latest");
    }
  }, [months, asOf]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSwitch = async (id) => {
    if (!id || id === session?.persona) return;
    await selectPersona(id);
    await qc.invalidateQueries();
    navigate("/");
  };

  const onReset = async () => {
    await resetDemoSession();
    await qc.invalidateQueries();
    navigate("/");
    toast.success("Demo session reset — re-entered as PMO Admin");
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#faf9f8" }}>
      <div
        data-testid="demo-banner"
        className="bg-[#EFF6FC] border-b border-[#C7E0F4] text-[#004578] px-4 py-2 text-xs font-semibold flex items-center justify-between gap-2 sticky top-0 z-40"
      >
        <span className="flex items-center gap-2">
          <ShieldCheck size={14} /> Demo — synthetic data; Microsoft integrations simulated
        </span>
        <span className="hidden sm:flex items-center gap-1 text-[#605E5C] font-normal">
          <Cloud size={13} /> Dataverse / Azure AD connections are mocked (not live)
        </span>
      </div>

      <header className="bg-white border-b border-[#E1DFDD] px-4 sm:px-6 py-2.5 flex flex-wrap items-center justify-between gap-3 z-30">
        <div className="flex items-center gap-3">
          <div className="h-7 w-7 rounded flex items-center justify-center text-white font-bold text-sm" style={{ background: "#0078D4" }}>
            P
          </div>
          <div>
            <div className="text-sm font-semibold text-[#201F1E] leading-tight">PMO Monthly Tracker</div>
            <div className="text-[11px] text-[#605E5C] leading-tight">Power Apps · Canvas App (demo)</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <div className="text-xs font-semibold text-[#201F1E]" data-testid="current-persona-label">
              {session?.label}
            </div>
            <div className="text-[11px] text-[#605E5C]">
              {session?.role === "pmo_admin" ? "Admin · " : "Read-only · "}
              Scope: {session?.platform === "all" ? "All platforms" : session?.platform}
            </div>
          </div>
          <div className="flex items-center gap-0.5" data-testid="header-month-nav">
            <button
              data-testid="header-month-prev"
              onClick={goOlder}
              disabled={!canOlder}
              title="Previous (older) month"
              className="inline-flex items-center justify-center h-9 w-8 rounded text-[#605E5C] hover:bg-[#F3F2F1] disabled:opacity-30 disabled:cursor-not-allowed fluent-focus"
            >
              <ChevronLeft size={16} />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="header-month-filter"
                  title="Choose month"
                  className="inline-flex items-center gap-1.5 h-9 px-2 rounded text-xs font-semibold text-[#323130] hover:bg-[#F3F2F1] fluent-focus"
                >
                  <CalendarRange size={16} className="text-[#0078D4]" />
                  <span className="hidden md:inline text-[#201F1E]">{currentMonthLabel}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 max-h-80 overflow-y-auto" data-testid="header-month-menu">
                <DropdownMenuLabel>Filter by month</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup value={asOf} onValueChange={setAsOf}>
                  <DropdownMenuRadioItem value="latest" data-testid="header-month-latest">
                    Latest available
                  </DropdownMenuRadioItem>
                  {months.map((m) => (
                    <DropdownMenuRadioItem key={m.month_key} value={m.month_key} data-testid={`header-month-${m.month_key}`}>
                      {m.month_label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              data-testid="header-month-next"
              onClick={goNewer}
              disabled={!canNewer}
              title="Next (newer) month"
              className="inline-flex items-center justify-center h-9 w-8 rounded text-[#605E5C] hover:bg-[#F3F2F1] disabled:opacity-30 disabled:cursor-not-allowed fluent-focus"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <div>
            <Select value={session?.persona ?? ""} onValueChange={onSwitch}>
              <SelectTrigger data-testid="persona-switcher" className="w-[190px] h-9 text-xs fluent-focus">
                <SelectValue placeholder="Switch persona (demo)" />
              </SelectTrigger>
              <SelectContent>
                {personas.map((p) => (
                  <SelectItem key={p.persona_id} value={p.persona_id} data-testid={`switch-${p.persona_id}`}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <button
            data-testid="reset-demo-session"
            onClick={onReset}
            title="Clear the demo session and re-enter as PMO Admin (does not delete data)"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0078D4] hover:text-[#004578] fluent-focus rounded px-2 py-1"
          >
            <RefreshCw size={13} /> Reset demo session
          </button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        <nav
          className={`${collapsed ? "w-14" : "w-56"} shrink-0 bg-white border-r border-[#E1DFDD] py-3 flex flex-col transition-all duration-200`}
          data-testid="nav-rail"
        >
          <div className="flex-1 flex flex-col gap-0.5 px-2">
            {NAV.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.id}
                  to={item.to}
                  end={item.end}
                  data-testid={`nav-${item.id}`}
                  className={({ isActive }) =>
                    `relative flex items-center gap-3 rounded px-3 py-2 text-sm fluent-focus transition-colors ${
                      isActive ? "bg-[#EFF6FC] text-[#004578] font-semibold" : "text-[#323130] hover:bg-[#F3F2F1]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-[#0078D4]" />}
                      <Icon size={18} className="shrink-0" />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
          <button
            onClick={() => setCollapsed((c) => !c)}
            data-testid="nav-collapse-toggle"
            className="mx-2 mt-2 flex items-center gap-2 rounded px-3 py-2 text-xs text-[#605E5C] hover:bg-[#F3F2F1] fluent-focus"
          >
            {collapsed ? <ChevronsRight size={16} /> : <><ChevronsLeft size={16} /> Collapse</>}
          </button>
        </nav>

        <main className="flex-1 min-w-0 p-4 sm:p-6 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
